import React, { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, User } from 'lucide-react';
import AdminPanel from './AdminPanel';
import { combineThemeClasses } from '../../utils/theme';
import { useAuth } from '../../contexts/AuthContext';

/**
 * AdminMenu - User menu component in the top-right of the menu bar
 * 
 * Simplified to show:
 * - Login button (for non-authenticated users)
 * - User profile + Site Settings + Logout (for authenticated admins)
 * - User profile + Logout (for authenticated non-admins)
 * 
 * All admin dashboards are now accessed through the unified AdminPanel.
 */
const AdminMenu = ({ theme = 'dark', onLaunchSDK }) => {
  const { user, login, logout, isLoading, error, devMode } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          disabled
          className={combineThemeClasses(theme, ['menuBar.button.base', 'menuBar.button.disabled'])}
        >
          <div className="w-8 h-8 rounded-full bg-gray-600 animate-pulse" />
        </button>
      </div>
    );
  }

  // Not logged in state - show Login button
  if (!user) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => login()}
          className={`${combineThemeClasses(theme, ['menuBar.button.base', 'menuBar.button.hover'])} flex items-center gap-2`}
          title="Login with Google"
        >
          {/* Mobile: just icon */}
          <User size={20} className="md:hidden text-gray-400" />
          {/* Desktop: icon + text */}
          <span className="hidden md:flex items-center gap-2">
            <User size={16} />
            {error ? (
              <span className="text-red-500">Auth Error</span>
            ) : (
              'Login'
            )}
          </span>
        </button>
        {error && (
          <div className="absolute top-full right-0 mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm whitespace-nowrap z-50">
            {error}
          </div>
        )}
      </div>
    );
  }

  // Logged in but NOT admin - show user info only, no settings
  if (!user.admin) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`${combineThemeClasses(theme, ['menuBar.button.base', 'menuBar.button.hover'])} flex items-center gap-2`}
          title={user?.email || 'User Menu'}
        >
          {/* Mobile: profile picture or user icon */}
          <div className="md:hidden">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt="" 
                className="w-8 h-8 rounded-full border-2 border-gray-600"
                referrerPolicy="no-referrer"
              />
            ) : (
              <User size={20} />
            )}
          </div>
          {/* Desktop: avatar + name */}
          <div className="hidden md:flex items-center gap-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-6 h-6 rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <User size={16} />
            )}
            <span className="text-xs text-gray-400">
              {user?.email?.split('@')[0]}
            </span>
            {devMode && (
              <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-yellow-500/20 text-yellow-400 rounded border border-yellow-500/30">
                DEV
              </span>
            )}
          </div>
        </button>

        {/* Non-admin dropdown: just logout */}
        {isOpen && (
          <div className={combineThemeClasses(theme, ['menuBar.dropdown.base'])}>
            <button
              onClick={() => {
                logout();
                setIsOpen(false);
              }}
              className={`${combineThemeClasses(theme, ['menuBar.dropdown.item'])} text-red-400 hover:text-red-300 hover:bg-red-400/10`}
            >
              <LogOut size={16} className="mr-2 inline" />
              Logout
            </button>
          </div>
        )}
      </div>
    );
  }

  // Logged in ADMIN state - show Site Settings + Logout
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${combineThemeClasses(theme, ['menuBar.button.base', 'menuBar.button.hover'])} flex items-center gap-2`}
        title={user?.email || 'Admin Menu'}
      >
        {/* Mobile: profile picture or settings icon */}
        <div className="md:hidden">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt="" 
              className="w-8 h-8 rounded-full border-2 border-gray-600"
              referrerPolicy="no-referrer"
            />
          ) : (
            <Settings size={20} />
          )}
        </div>
        {/* Desktop: avatar + name */}
        <div className="hidden md:flex items-center gap-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-6 h-6 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <Settings size={16} />
          )}
          <span className="text-xs text-gray-400">
            {user?.email?.split('@')[0]}
          </span>
          {devMode && (
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-yellow-500/20 text-yellow-400 rounded border border-yellow-500/30">
              DEV
            </span>
          )}
        </div>
      </button>

      {/* Admin dropdown: Site Settings + Logout */}
      {isOpen && (
        <div className={combineThemeClasses(theme, ['menuBar.dropdown.base'])}>
          <button
            onClick={() => {
              setShowAdminPanel(true);
              setIsOpen(false);
            }}
            className={combineThemeClasses(theme, ['menuBar.dropdown.item'])}
          >
            <Settings size={16} className="mr-2 inline" />
            Site Settings
          </button>
          
          <div className={combineThemeClasses(theme, ['menuBar.dropdown.divider'])} />
          
          <button
            onClick={() => {
              logout();
              setIsOpen(false);
            }}
            className={`${combineThemeClasses(theme, ['menuBar.dropdown.item'])} text-red-400 hover:text-red-300 hover:bg-red-400/10`}
          >
            <LogOut size={16} className="mr-2 inline" />
            Logout
          </button>
        </div>
      )}

            {/* Admin Panel Modal */}
            {showAdminPanel && (
              <AdminPanel
                onClose={() => setShowAdminPanel(false)}
                theme={theme}
                onLaunchSDK={onLaunchSDK}
              />
            )}
    </div>
  );
};

export default AdminMenu;
